import React from "react";

import Button from "@busbud/design-system-components/dist/Button";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import type * as UDM from "@busbud/int-schemas/udm";

import { useAppContext } from "@app/helpers/hooks";
import type { SeatPrices } from "@app/modules/results/components/departure/seat-selection-details/seat-price";
import { SeatPrice } from "@app/modules/results/components/departure/seat-selection-details/seat-price";
import type { CurrencyCode } from "@app/types/currency";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import { useStyles } from "./drawer.styles";
import { Seats } from "./seats";

type Props = {
  passengers: PassengerSeatSelectionInstance[];
  selected_seats: UDM.SeatCell[];
  currency: CurrencyCode;
  disabled: boolean;
  onConfirm: () => void;
  prices: SeatPrices;
  segment_id: string;
  passenger_index?: number;
};

export const Drawer: React.FC<Props> = props => {
  const { translator } = useAppContext();
  const {
    passengers,
    selected_seats,
    currency,
    disabled,
    onConfirm,
    prices,
    segment_id
  } = props;

  const { classes } = useStyles();
  const is_every_passenger_assigned =
    passengers.length === selected_seats.length;
  const allow_single_passenger_selection =
    typeof props.passenger_index !== "undefined";

  return (
    <Stack direction="column" className={classes.container}>
      <div className={classes.wrapperSeatSelection}>
        <Stack
          alignItems="center"
          spacing={1.5}
          className={classes.centeredContent}
        >
          <Label size="xs" fontWeight="bold">
            {translator.t("!checkout.seat-selection.your-seats")}
          </Label>
          <Seats
            selected_seats={selected_seats}
            passengers_count={passengers.length}
          />
        </Stack>
      </div>
      <div className={classes.wrapperTotal}>
        <Stack
          justifyContent="space-between"
          alignItems="center"
          className={classes.centeredContent}
        >
          <Stack direction="column">
            <Label size="xs" fontWeight="bold">
              {translator.t("!checkout.seat-selection.total-price")}
            </Label>
            <Label size="xl" data-test-id="seat-price">
              <SeatPrice
                currency={currency}
                passengers={passengers}
                selected_seats={selected_seats}
                prices={prices}
                segment_id={segment_id}
              />
            </Label>
          </Stack>
          <Button
            className={classes.button}
            variant="contained"
            size="md"
            color="primary"
            fullWidth
            disabled={
              (!is_every_passenger_assigned &&
                !allow_single_passenger_selection) ||
              disabled
            }
            onClick={onConfirm}
            data-cy="select-seat-departure"
          >
            {is_every_passenger_assigned && !disabled
              ? translator.t("!checkout.seat-selection.confirm-and-continue")
              : translator.t("!checkout.seat-selection.select-this-seat")}
          </Button>
        </Stack>
      </div>
    </Stack>
  );
};
